(() => {
  'use strict';

  const setQuantity = (scope, quantity) => {
    scope.querySelectorAll('input[name="quantity"]').forEach((input) => {
      input.value = quantity;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  };

  const initBundlePickers = (root = document) => {
    root.querySelectorAll('[data-value-bundles]:not([data-initialized])').forEach((picker) => {
      picker.dataset.initialized = 'true';
      const blocks = picker.closest('.main-product__blocks');
      if (blocks) {
        blocks.classList.add('kaiora-has-value-bundles');
      }
      picker.querySelectorAll('[data-bundle-quantity]').forEach((option) => {
        option.addEventListener('click', () => {
          picker.querySelectorAll('[data-bundle-quantity]').forEach((item) => {
            item.classList.toggle('is-selected', item === option);
            item.setAttribute('aria-pressed', item === option ? 'true' : 'false');
          });
          const section = picker.closest('.section__wrapper') || document;
          setQuantity(section, Number(option.dataset.bundleQuantity) || 1);
        });
      });
    });
  };

  const initComplementary = (root = document) => {
    root.querySelectorAll('[data-complementary-products]:not([data-initialized])').forEach((section) => {
      section.dataset.initialized = 'true';
      const button = section.querySelector('[data-add-selected]');
      const status = section.querySelector('[data-complementary-status]');
      const totalElement = section.querySelector('[data-complementary-total]');
      const oldTotalElement = section.querySelector('[data-complementary-old-total]');
      const savingElement = section.querySelector('[data-complementary-saving]');
      const savingValueElement = section.querySelector('[data-complementary-saving-value]');
      const countElement = section.querySelector('[data-complementary-count]');
      const inputs = [...section.querySelectorAll('[data-complementary-item]')];
      const discount = Math.max(0, Math.min(100, Number(section.dataset.discount) || 0));
      const moneyFormat = section.dataset.moneyFormat || '${{amount}}';
      const currency = section.dataset.currency;
      const locale = section.dataset.locale || document.documentElement.lang || 'en';
      if (!button) return;

      const formatMoney = (cents) => {
        if (currency && typeof Intl !== 'undefined' && Intl.NumberFormat) {
          return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
        }
        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
          return window.Shopify.formatMoney(cents, moneyFormat);
        }
        const amount = (cents / 100).toFixed(2);
        return moneyFormat
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(cents / 100)))
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
          .replace(/\{\{\s*amount\s*\}\}/g, amount);
      };

      const updateSummary = () => {
        const selected = inputs.filter((input) => input.checked);
        const subtotal = selected.reduce((sum, input) => sum + Number(input.dataset.price || 0), 0);
        const compareTotal = selected.reduce((sum, input) => sum + Number(input.dataset.compareAt || input.dataset.price || 0), 0);
        const discountedTotal = Math.round(subtotal * (1 - discount / 100));
        const savings = Math.max(0, compareTotal - discountedTotal);

        if (totalElement) totalElement.textContent = formatMoney(discountedTotal);
        if (oldTotalElement) {
          oldTotalElement.textContent = formatMoney(compareTotal);
          oldTotalElement.hidden = savings <= 0;
        }
        if (savingElement) savingElement.hidden = savings <= 0;
        if (savingValueElement) savingValueElement.textContent = formatMoney(savings);
        if (countElement) countElement.textContent = `${selected.length} ${selected.length === 1 ? 'product' : 'products'} selected`;
        button.disabled = selected.length === 0;
      };

      inputs.forEach((input) => {
        input.addEventListener('change', () => {
          if (input.hasAttribute('data-required') && !input.checked) input.checked = true;
          input.closest('.kaiora-complementary__item')?.classList.toggle('is-selected', input.checked);
          if (status) status.textContent = '';
          updateSummary();
        });
      });

      updateSummary();

      button.addEventListener('click', async () => {
        const items = [...section.querySelectorAll('[data-complementary-item]:checked')].map((input) => ({
          id: Number(input.value),
          quantity: 1,
        }));

        if (!items.length) {
          if (status) status.textContent = 'Choose at least one product.';
          return;
        }

        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        if (status) status.textContent = 'Adding products…';

        try {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });
          if (!response.ok) throw new Error('Unable to add the selected products.');
          window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
        } catch (error) {
          if (status) status.textContent = error.message;
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      });
    });
  };

  const initReviewToggles = (root = document) => {
    root.querySelectorAll('[data-review-toggle]:not([data-initialized])').forEach((button) => {
      button.dataset.initialized = 'true';
      button.addEventListener('click', () => {
        const section = button.closest('.kaiora-product-reviews');
        if (!section) return;
        section.classList.toggle('is-expanded');
        const expanded = section.classList.contains('is-expanded');
        button.textContent = expanded ? button.dataset.labelLess : button.dataset.labelMore;
        button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });
    });
  };

  const initShopifyFormsOffset = () => {
    if (window.__kaioraShopifyFormsOffsetInitialized) return;
    window.__kaioraShopifyFormsOffsetInitialized = true;

    const styleId = 'kaiora-sticky-cart-offset';
    const styleText = `
      @media (max-width: 63.99em) {
        #app-embed[class*="_appEmbedRootTeaser_"] {
          bottom: auto !important;
          box-sizing: border-box !important;
          left: 50% !important;
          padding: 0 !important;
          right: auto !important;
          top: var(--kaiora-promo-top, 7.5rem) !important;
          transform: translateX(-50%) !important;
          width: calc(100vw - 1rem) !important;
        }

        #app-embed[class*="_appEmbedRootTeaser_"] [class*="_teaserContainer_"] {
          max-width: none !important;
          width: 100% !important;
        }

        #app-embed[class*="_appEmbedRootTeaser_"] [class*="_teaserHeading_"] {
          font-size: 19px !important;
          max-width: none !important;
          white-space: nowrap !important;
        }
      }
    `;

    const installOffset = () => {
      const header = document.querySelector('.header') || document.querySelector('#nav-wrapper');
      const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 112;

      document.querySelectorAll('shopify-forms-embed').forEach((host) => {
        host.style.setProperty('--kaiora-promo-top', `${Math.round(headerBottom)}px`);
        const shadowRoot = host.shadowRoot;
        if (!shadowRoot || shadowRoot.getElementById(styleId)) return;

        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = styleText;
        shadowRoot.appendChild(style);
      });
    };

    installOffset();

    const observer = new MutationObserver(installOffset);
    observer.observe(document.documentElement, { childList: true, subtree: true });
    const retryInterval = window.setInterval(installOffset, 500);
    let offsetFrame = 0;
    const scheduleOffsetUpdate = () => {
      if (offsetFrame) return;
      offsetFrame = window.requestAnimationFrame(() => {
        offsetFrame = 0;
        installOffset();
      });
    };
    window.addEventListener('resize', scheduleOffsetUpdate, { passive: true });
    window.addEventListener('scroll', scheduleOffsetUpdate, { passive: true });
    window.setTimeout(() => {
      observer.disconnect();
      window.clearInterval(retryInterval);
    }, 20000);
  };

  const initProductGalleryHeaderOffset = (root = document) => {
    const galleries = [...root.querySelectorAll('.main-product__gallery-sticky')];
    if (!galleries.length) return;

    const header = document.getElementById('nav-wrapper');
    if (!header) return;

    const updateOffset = () => {
      const headerHeight = Math.ceil(header.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--kaiora-product-header-height', `${headerHeight}px`);
    };

    updateOffset();

    if (!window.__kaioraProductGalleryHeaderOffsetInitialized) {
      window.__kaioraProductGalleryHeaderOffsetInitialized = true;
      window.addEventListener('resize', updateOffset, { passive: true });
      document.fonts?.ready.then(updateOffset);

      if ('ResizeObserver' in window) {
        const headerObserver = new ResizeObserver(updateOffset);
        headerObserver.observe(header);
        window.__kaioraProductGalleryHeaderObserver = headerObserver;
      }
    }
  };

  const initialize = (root = document) => {
    initBundlePickers(root);
    initComplementary(root);
    initReviewToggles(root);
    initShopifyFormsOffset();
    initProductGalleryHeaderOffset(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
